import React, { useState, useEffect, useRef } from 'react';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, subWeeks, addWeeks } from 'date-fns';
import { db } from '../services/db';
import { TimeEntry, TimesheetSubmission } from '../models/types';
import { formatTime, calculateNetWorkDuration } from '../utils/timeUtils';
import { exportToCsv, generateTimesheetPdf } from '../utils/exportUtils'; // Using PDF format for exports
import { syncTimeEntries } from '../services/syncService';
import SignatureCanvas from './SignatureCanvas';
import '../styles/WeeklyTimesheet.css';

// This space intentionally left blank

interface WeeklyTimesheetProps {
  onClose: () => void;
}

interface DayEntries {
  date: Date;
  entries: TimeEntry[];
  totalMinutes: number;
}

interface SubmissionStatus {
  isSubmitting: boolean;
  isSuccess: boolean;
  isError: boolean;
  message: string;
}

const WeeklyTimesheet: React.FC<WeeklyTimesheetProps> = ({ onClose }) => {
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(startOfWeek(new Date()));
  const [weeklyData, setWeeklyData] = useState<DayEntries[]>([]);
  // Deleted-but-not-yet-synced entries for this week. Held apart from
  // weeklyData so nothing that renders or totals can pick them up by accident,
  // and re-joined only where the dashboard needs them — see syncableEntries.
  const [pendingDeletes, setPendingDeletes] = useState<TimeEntry[]>([]);
  const [userName, setUserName] = useState<string>('');
  const [totalHours, setTotalHours] = useState<number>(0);
  const timesheetRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [signature, setSignature] = useState<string>('');
  const [signatureDate, setSignatureDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  // Removed showSubmissionModal state
  const [submissionStatus, setSubmissionStatus] = useState<SubmissionStatus>({
    isSubmitting: false,
    isSuccess: false,
    isError: false,
    message: ''
  });
  const [existingSubmission, setExistingSubmission] = useState<TimesheetSubmission | undefined>(undefined);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [syncMessage, setSyncMessage] = useState<string>('');

  console.log('[WeeklyTimesheet Render] currentWeekStart:', currentWeekStart);
  console.log('[WeeklyTimesheet Render] existingSubmission:', existingSubmission);
  console.log('[WeeklyTimesheet Render] signature (from canvas):', signature);
  console.log('[WeeklyTimesheet Render] isLoading:', isLoading);
  console.log('[WeeklyTimesheet Render] submissionStatus:', submissionStatus);

  useEffect(() => {
    console.log('[WeeklyTimesheet Effect] currentWeekStart changed:', currentWeekStart);
    loadUserName();
    loadWeeklyData();
    checkExistingSubmission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeekStart]);

  const loadUserName = async () => {
    try {
      const name = await db.getUserName();
      setUserName(name || 'Anonymous');
    } catch (error) {
      console.error('Error loading user name:', error);
    }
  };

  const checkExistingSubmission = async () => {
    try {
      const submission = await db.getTimesheetSubmission(currentWeekStart);
      console.log('[checkExistingSubmission] Fetched submission for', currentWeekStart, ':', submission);
      setExistingSubmission(submission);
    } catch (error) {
      console.error('Error checking existing submission:', error);
    }
  };

  const loadWeeklyData = async () => {
    setIsLoading(true);
    try {
      const weekEnd = endOfWeek(currentWeekStart);
      const daysInWeek = eachDayOfInterval({ start: currentWeekStart, end: weekEnd });
      
      let weeklyTotal = 0;
      const daysData: DayEntries[] = [];
      // Tombstones must never be shown or counted, but they are exactly what
      // sync exists to deliver, so they are kept in a second list rather than
      // discarded. Filtering only at the sync call sites would be the wrong way
      // round: a display path that forgets shows a deleted shift, which is
      // cosmetic; a sync path that forgets strands the deletion forever.
      const pendingDeletes: TimeEntry[] = [];

      for (const day of daysInWeek) {
        const dayStart = new Date(day);
        dayStart.setHours(0, 0, 0, 0);

        const dayEnd = new Date(day);
        dayEnd.setHours(23, 59, 59, 999);

        const allForDay = await db.timeEntries
          .where('timeIn')
          .between(dayStart, dayEnd, true, true)
          .toArray();

        const entries: TimeEntry[] = [];
        for (const entry of allForDay) {
          if (entry.deletedAt) pendingDeletes.push(entry);
          else entries.push(entry);
        }

        let dayTotal = 0;
        entries.forEach(entry => {
          // calculateNetWorkDuration already checks for entry.timeOut and returns 0 if not present
          dayTotal += calculateNetWorkDuration(entry);
        });

        weeklyTotal += dayTotal;
        daysData.push({
          date: day,
          entries,
          totalMinutes: dayTotal
        });
      }

      setWeeklyData(daysData);
      setPendingDeletes(pendingDeletes);
      setTotalHours(weeklyTotal / 60); // Convert minutes to hours
    } catch (error) {
      console.error('Error loading weekly data:', error);
    } finally {
      setIsLoading(false);
    }
  };


  /**
   * What sync gets: the displayed week PLUS this week's tombstones. Every
   * call to syncTimeEntries must go through here. syncTimeEntries applies
   * isEntryUnsynced itself, so passing already-synced rows is harmless —
   * omitting a tombstone is not, because nothing else will ever send it.
   */
  const syncableEntries = (): TimeEntry[] =>
    weeklyData.flatMap(day => day.entries).concat(pendingDeletes);

  const handlePreviousWeek = () => {
    setCurrentWeekStart(subWeeks(currentWeekStart, 1));
  };

  const handleNextWeek = () => {
    const nextWeek = addWeeks(currentWeekStart, 1);
    const today = new Date();
    
    // Don't allow navigating to future weeks
    if (nextWeek <= today) {
      setCurrentWeekStart(nextWeek);
    }
  };

  // Verification toggle functionality - currently unused but kept for future implementation
  // const handleToggleVerification = async (entryId: number, verified: boolean) => {
  //   try {
  //     await db.updateEntryVerification(entryId, verified);
  //     await loadWeeklyData(); // Reload data to reflect changes
  //   } catch (error) {
  //     console.error('Error updating verification status:', error);
  //   }
  // };

  const handleSignatureChange = (signatureData: string) => {
    setSignature(signatureData);
  };

  const handleSignatureDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSignatureDate(e.target.value);
  };

  const handleSubmitTimesheet = async () => {
    if (!signature) {
      alert('Please sign the timesheet before submitting.');
      return;
    }
    
    // Ensure signature date is set
    if (!signatureDate) {
      setSignatureDate(format(new Date(), 'yyyy-MM-dd'));
    }
    
    await saveTimesheetLocally();
  };

  const saveTimesheetLocally = async () => {
    setSubmissionStatus({
      isSubmitting: true,
      isSuccess: false,
      isError: false,
      message: 'Processing your timesheet...'
    });
    
    try {
      if (!timesheetRef.current) throw new Error('Timesheet reference not found');
      
      // Generate and download the PDF with signature
      const allEntries = weeklyData.flatMap(day => day.entries);
      generateTimesheetPdf(
        allEntries, 
        userName, 
        currentWeekStart,
        signature,
        signatureDate
      );
      
      // Export CSV as well
      exportToCsv(allEntries);
      
      // Sync to dashboard
      let syncResult = null;

      setSubmissionStatus({
        isSubmitting: true,
        isSuccess: false,
        isError: false,
        message: 'Syncing to dashboard...'
      });

      syncResult = await syncTimeEntries(syncableEntries());

      // Save submission to database
      const weekEnd = endOfWeek(currentWeekStart);
      const submission: TimesheetSubmission = {
        weekStartDate: currentWeekStart,
        weekEndDate: weekEnd,
        submissionDate: new Date(),
        signature,
        signatureDate: signatureDate || format(new Date(), 'yyyy-MM-dd'),
        totalHours,
        submittedBy: userName,
        submissionMethod: syncResult?.success ? 'dashboard' : 'local',
        status: 'submitted' as const
      };
      
      await db.saveTimesheetSubmission(submission);
      setExistingSubmission(submission);
      
      let successMessage = 'Timesheet saved successfully! PDF and CSV files have been downloaded to your device.';
      
      if (syncResult?.success) {
        // A refusal comes back inside a SUCCESSFUL sync — the office
        // accepted the batch and reverted one row in it. Saying only
        // "synced to the dashboard" would be a false all-clear on the one
        // path an employee uses when they are finished for the week.
        if (syncResult.conflicts?.length) {
          const first = syncResult.conflicts[0];
          const what = first.reason === 'paid' ? 'already been paid out' : 'already been invoiced';
          successMessage +=
            ` Entries were synced, but ${first.date} couldn't be changed — it's ${what}.` +
            ` Your change was undone. Ask the office if it needs fixing.`;
        } else {
          successMessage += ' Time entries have been synced to the dashboard.';
        }
      } else {
        successMessage += ` Sync to dashboard failed: ${syncResult?.message || 'Unknown error'}. Data saved locally.`;
      }

      setSubmissionStatus({
        isSubmitting: false,
        isSuccess: true,
        isError: false,
        message: successMessage
      });

      // The sync may have applied tombstones or reverted a locked row; the
      // week on screen is stale either way.
      await loadWeeklyData();
    } catch (error) {
      console.error('Error saving timesheet:', error);
      setSubmissionStatus({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        message: 'Failed to save timesheet. Please try again.'
      });
    }
  };

  const handleExportToCsv = () => {
    try {
      // Get all entries for the week and sort them by date
      const allEntries = weeklyData
        .flatMap(day => day.entries)
        .sort((a, b) => new Date(a.timeIn).getTime() - new Date(b.timeIn).getTime());
      
      // Export to CSV with the new format
      exportToCsv(allEntries);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      alert('Failed to export to CSV. Please try again.');
    }
  };

  const handleManualSync = async () => {
    setIsSyncing(true);
    setSyncMessage('Syncing entries to dashboard...');

    try {
      // Tombstones included: a week whose only outstanding change is a
      // deletion still has something to send, and sending nothing would leave
      // that deletion stuck on the phone forever.
      const allEntries = syncableEntries();
      if (allEntries.length === 0) {
        setSyncMessage('No entries to sync for this week.');
        setIsSyncing(false);
        return;
      }

      // syncTimeEntries drops entries the dashboard already has, so the count
      // it reports is the count actually sent — allEntries.length is the whole
      // displayed week and would overstate it.
      const result = await syncTimeEntries(allEntries);

      if (result.success) {
        // A conflict means the office refused part of what we sent (the row
        // is already invoiced or paid) and reverted it — that must win over
        // the generic success message, or the employee never learns their
        // change didn't stick.
        if (result.conflicts?.length) {
          const first = result.conflicts[0];
          const what = first.reason === 'paid' ? 'already been paid out' : 'already been invoiced';
          setSyncMessage(
            `${first.date} couldn't be changed — it's ${what}. ` +
            `Your change was undone. Ask the office if it needs fixing.`
          );
        } else {
          setSyncMessage(result.message);
        }
      } else {
        setSyncMessage(`Sync failed: ${result.message}`);
      }
    } catch (error) {
      setSyncMessage(`Sync error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      // A confirmed tombstone is hard-deleted and a refused edit is reverted to
      // the office's values, both behind our back — reload or the screen keeps
      // showing what the phone believed before it asked.
      await loadWeeklyData();
      setIsSyncing(false);
      // Clear message after 5 seconds
      setTimeout(() => setSyncMessage(''), 5000);
    }
  };

  const handlePrintPDF = async () => {
    try {
      // Get all entries for the week and sort them by date
      const allEntries = weeklyData
        .flatMap(day => day.entries)
        .sort((a, b) => new Date(a.timeIn).getTime() - new Date(b.timeIn).getTime());
      generateTimesheetPdf(
        allEntries,
        userName,
        currentWeekStart,
        signature,
        signatureDate
      );
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Failed to export to PDF. Please try again.');
    }
  };

  const formatWeekRange = () => {
    const weekEnd = endOfWeek(currentWeekStart);
    return `${format(currentWeekStart, 'MMM d, yyyy')} - ${format(weekEnd, 'MMM d, yyyy')}`;
  };

  if (isLoading) {
    return <div className="weekly-timesheet-loading">Loading timesheet data...</div>;
  }

  return (
    <div className="weekly-timesheet-container">
      <div className="weekly-timesheet-header">
        <h2>Weekly Timesheet</h2>
        <div className="week-navigation">
          <button onClick={handlePreviousWeek}>&lt; Previous Week</button>
          <span className="week-range">{formatWeekRange()}</span>
          <button 
            onClick={handleNextWeek}
            disabled={currentWeekStart >= startOfWeek(new Date())}
          >
            Next Week &gt;
          </button>
        </div>
      </div>
      
      <div className="timesheet-actions">
        <button className="export-button" onClick={handleExportToCsv}>
          Export For Excel
        </button>
        <button 
          className="print-button" 
          onClick={handlePrintPDF}
          disabled={isLoading || submissionStatus.isSubmitting}
        >
          Export to PDF
        </button>
        <button 
          className="sync-button" 
          onClick={handleManualSync}
          disabled={isLoading || isSyncing}
        >
          {isSyncing ? 'Syncing...' : 'Sync to Dashboard'}
        </button>
        {existingSubmission ? (
          <div className="submission-info">
            <span className="submission-status">
              Saved on {format(new Date(existingSubmission.submissionDate), 'MMM d, yyyy')}
            </span>
          </div>
        ) : (
          <button 
            className="submit-button" 
            onClick={handleSubmitTimesheet}
            disabled={isLoading || submissionStatus.isSubmitting || (!existingSubmission && !signature)}
          >
            Save Timesheet
          </button>
        )}
        <button className="close-button" onClick={onClose}>
          Close
        </button>
      </div>
      
      {submissionStatus.isSuccess && (
        <div className="submission-success">
          <p>{submissionStatus.message}</p>
        </div>
      )}
      
      {submissionStatus.isError && (
        <div className="submission-error">
          <p>{submissionStatus.message}</p>
        </div>
      )}
      
      {syncMessage && (
        <div className={`sync-message ${syncMessage.includes('Successfully') ? 'success' : syncMessage.includes('failed') || syncMessage.includes('error') ? 'error' : 'info'}`}>
          <p>{syncMessage}</p>
        </div>
      )}
      
      <div className="timesheet-content" ref={timesheetRef}>
        <div className="timesheet-info">
          <div className="employee-info">
            <h3>Employee: {userName}</h3>
            <p>Week: {formatWeekRange()}</p>
          </div>
          <div className="total-hours">
            <h3>Total Hours: {totalHours.toFixed(2)}</h3>
          </div>
        </div>
        
        <table className="weekly-table">
          <thead>
            <tr>
              <th>Project</th>
              <th>Task</th>
              <th>Start</th>
              <th>End</th>
              <th>Total</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {weeklyData.map((day) => {
              // Find earliest start and latest end for the day
              const entries = day.entries;
              let earliestStart = '';
              let latestEnd = '';
              if (entries.length > 0) {
                earliestStart = formatTime(
                  entries.reduce((min, e) => e.timeIn < min ? e.timeIn : min, entries[0].timeIn)
                );
                latestEnd = formatTime(
                  entries.reduce((max, e) => (e.timeOut && e.timeOut > max ? e.timeOut : max), entries[0].timeIn)
                );
              }
              return (
                <React.Fragment key={day.date.toISOString()}>
                  <tr className="date-row">
                    <td colSpan={6}>
                      {format(day.date, 'EEEE')}, Start {earliestStart || '--:--'} - End {latestEnd || '--:--'} Total: {(day.totalMinutes/60).toFixed(2)}
                    </td>
                  </tr>
                  {entries.length > 0 ? (
                    entries.map((entry) => (
                      <tr key={entry.id} className="entry-row">
                        <td>{entry.project}</td>
                        <td>{entry.task || '--'}</td>
                        <td>{formatTime(entry.timeIn)}</td>
                        <td>{entry.timeOut ? formatTime(entry.timeOut) : '--:--'}</td>
                        <td>{entry.timeOut ? (calculateNetWorkDuration(entry) / 60).toFixed(2) : '--'}</td>
                        <td>{entry.notes || '--'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr className="no-entries-row">
                      <td colSpan={6}>No entries for this day</td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5} className="weekly-total-label">Weekly Total Hours:</td>
              <td colSpan={2} className="weekly-total-value">{totalHours.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
        
        {!existingSubmission && (
          <div className="timesheet-signature-section">
            <h3>Timesheet Verification</h3>
            <p>I certify that the above hours are correct and complete.</p>
            
            <div className="signature-container">
              <div className="signature-field">
                <label>Employee Signature:</label>
                <SignatureCanvas 
                  onSignatureChange={handleSignatureChange} 
                  width={400} 
                  height={150}
                  mobileOptimized={true}
                />
              </div>
              
              <div className="signature-date-field">
                <label>Date:</label>
                <input 
                  type="date" 
                  value={signatureDate}
                  onChange={handleSignatureDateChange}
                />
              </div>
            </div>
          </div>
        )}
        
        {existingSubmission && existingSubmission.signature && (
          <div className="timesheet-signature-section">
            <h3>Timesheet Verification</h3>
            <p>This timesheet was signed and submitted on {format(new Date(existingSubmission.submissionDate), 'MMMM d, yyyy')}.</p>
            
            <div className="signature-container">
              <div className="signature-field">
                <label>Employee Signature:</label>
                <img 
                  src={existingSubmission.signature} 
                  alt="Signature" 
                  className="saved-signature"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeeklyTimesheet;
